import { FC, useEffect, useState } from 'react'
import { PagedResponseModel } from '../../../../models/responses/PagedResponseModel'
import { IPaginationFilter } from '../../../../models/requests/PaginationFilter'
import { GenericTable } from '../../../components/tables/GenericTable'
import { TableHelperButtons } from '../../../components/buttons/TableHelperButtons'
import { TableHelperButtonEnum } from '../../../enums/TableHelperButtonEnum'
import { SelectModel } from '../../../models/SelectModel'
import RoleApi from '../../../../infrastructure/api/RoleApi'
import { ITaskDefineModel, ITaskModel } from '../../../../models/responses/TaskModel'
import TaskApi from '../../../../infrastructure/api/TaskApi'
import { RootState } from '../../../../setup'
import { useSelector, shallowEqual } from 'react-redux'
import { IUserDictionaryModel } from '../../../../models/responses/UserDictionaryModel'
import UserApi from '../../../../infrastructure/api/UserApi'

type Props = {
  onClickItem: (itemId: any, itemName: string, type: string) => void
  refresh: number
}

const TaskListModal: FC<Props> = ({ onClickItem, refresh }) => {
  const [tasks, setTasks] = useState<PagedResponseModel<ITaskModel[]>>()
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchText, setSearchText] = useState('')
  const [sort, setSort] = useState('date_desc')
  const [roles, setRoles] = useState<SelectModel[]>([])

  const [users, setUsers] = useState<IUserDictionaryModel[]>([])

  const taskDefines: ITaskDefineModel[] = useSelector<RootState>(({ task }) => task.taskDefines, shallowEqual) as ITaskDefineModel[]

  const getUsers = async (userIds: string[]) => {
    var responseUsers = await UserApi.GetUserDictionaryIds(userIds)
    var dataUsers: IUserDictionaryModel[] = responseUsers.data
    setUsers([...users, ...dataUsers]);
  }

  async function getTasks() {

    var responseRoles = await RoleApi.GetRoleDictionary()
    var dataRoles: SelectModel[] = [...responseRoles.data.map(d => ({
      value: d.id.toString(),
      label: d.name
    }))]
    setRoles(dataRoles)

    const paginationFilter: IPaginationFilter = {
      page: pageNumber,
      size: pageSize,
      orderBy: sort,
      search: searchText,
    }
    var response = await TaskApi.GetTasks(paginationFilter)

    getUsers([...response.data.items.flatMap(s => s.assignedUsers), ...response.data.items.flatMap(s => s.associatedUsers), ...response.data.items.flatMap(s => s.createdBy)])
    setTasks(response.data)
  }

  useEffect(() => {

    getTasks()
  }, [refresh, pageNumber, pageSize, searchText, sort])

  return (
    <>
      <GenericTable
        title='Tasks'
        data={tasks?.items}
        currentTotalRecords={tasks?.totalRecords!!}
        currentPageNumber={tasks?.pageNumber!!}
        columns={[
          {
            label: '#',
            name: '#',
            options: {
              customBodyRenderLite: (dataIndex) => {
                return <>{(pageNumber - 1) * pageSize + ++dataIndex}</>
              },
              sort: false,
              viewColumns: false,
            },
          },
          {
            label: 'Subject / Creator',
            name: 'taskDefineId',
            options: {
              customBodyRenderLite: (dataIndex) => {
                return <div>{taskDefines.find(i => i.id == tasks?.items[dataIndex].taskDefineId)?.title.toUpperCase()}
                  <br />
                  <span className='badge badge-light-success mt-1'>{users.find(i => i.id == tasks?.items[dataIndex].createdBy)?.email}</span>
                </div>
              },
            },
          },
          {
            label: 'Assigned',
            name: 'assignedUsers',
            options: {
              customBodyRender: (value: string[]) => {
                return <>{value!!.map((item, key) => {
                  return (
                    <div key={key}><span className='badge badge-light-primary mb-1'>{users.find(i => i.id == item)?.email}</span><br /></div>
                  )
                }
                )
                }</>
              },
            },
          },
          {
            label: 'Associated',
            name: 'associatedUsers',
            options: {
              customBodyRender: (value: string[]) => {
                return <>{value!!.map((item, key) => {
                  return (
                    <div key={key}><span className='badge badge-light-primary mb-1'>{users.find(i => i.id == item)?.email}</span><br /></div>
                  )
                }
                )
                }</>
              },
            },
          },
          {
            label: 'Transaction',
            name: '#',
            options: {
              customBodyRenderLite: (dataIndex) => {
                return (<>
                  {<div className='d-flex align-items-center'>
                    <span className="badge badge-light-dark">
                      {tasks?.items[dataIndex].transactionNumber}
                    </span>
                  </div>}
                </>)
              },
            },
          },
          {
            label: "Status",
            name: '#',
            options: {
              customBodyRenderLite: (dataIndex) => {
                return (<>
                  {tasks?.items[dataIndex].isCompleted ? <label className='badge badge-light-primary'>Completed</label> : <label className='badge badge-light-info'>Wait</label>}
                </>)
              }
            }
          },

          {
            label: 'Expiration At',
            name: 'expirationAt'
          },
          {
            label: '#',
            name: 'Id',
            options: {
              customHeadLabelRender: (options) => {
                return (
                  <div className='d-flex justify-content-end flex-shrink-0'>{options.label}</div>
                )
              },
              customBodyRenderLite: (dataIndex) => {
                return (
                  <TableHelperButtons
                    itemId={tasks?.items[dataIndex]?.id!!}
                    itemParam={taskDefines.find(i => i.id == tasks?.items[dataIndex].taskDefineId)?.title}
                    types={[TableHelperButtonEnum.View, TableHelperButtonEnum.Update, TableHelperButtonEnum.Delete]}
                    onClickItem={onClickItem}
                  />
                )
              },
              filter: false,
              sort: false,
              viewColumns: false,
            },
          },
        ]}
        customOptions={{
          rowsPerPage: pageSize,
          customTableBodyFooterRender: (opts) => {
            return <div></div>
          },
        }}
        setPageNumber={setPageNumber}
        setPageSize={setPageSize}
        setSearchText={setSearchText}
        setSort={setSort}
      />
    </>
  )
}

export { TaskListModal }
